import * as styles from './styles';
import { Table as BaseTable, Box, TableBody, Typography } from '@material-ui/core';

import type { PastMaintenance, PastMaintenanceWithVehicle } from '@cobliteam/api-lib';

import { DescriptionListDefault, type ITableColumn, type ITableRow, Label, PlaceholderText, TableCell, TableHead, TableRow } from '@cobliteam/base-components';

import { Button } from '@cobliteam/ui/components';

import { EditRounded } from '@mui/icons-material';

import { currencyFormatter } from '../../../../utils';

enum ItemId {
  NAME = 'name',
  COST = 'cost',
  REMARK = 'remark',
}

interface ItemRow extends ITableRow {
  name: string;
  cost?: number;
  remark?: string;
}

interface ItemColumn extends ITableColumn<ItemRow> {
  id: ItemId;
  label: string;
}

const itemTableColumns: ItemColumn[] = [
  {
    id: ItemId.NAME,
    label: 'Nome',
    className: styles.columnWithMaxWidth('30px'),
    Cell: (row: ItemRow) => row.name,
  },
  {
    id: ItemId.COST,
    label: 'Custo do item',
    className: styles.columnWithMaxWidth('30px'),
    Cell: (row: ItemRow) => (row?.cost ? `R$ ${currencyFormatter.format(row?.cost)}` : <PlaceholderText>Não registrado</PlaceholderText>),
  },
  {
    id: ItemId.REMARK,
    label: 'Observações',
    className: styles.columnWithMaxWidth('0px'),
    Cell: (row: ItemRow) => (row?.remark ? row?.remark : <PlaceholderText>Nenhuma descrição</PlaceholderText>),
  },
];

interface VehicleMaintenanceItemsTableProps {
  pastMaintenance: PastMaintenanceWithVehicle;
  onEditClick: (maintenance: PastMaintenance) => void;
}

const VehicleMaintenanceItemsTable = ({ pastMaintenance, onEditClick }: VehicleMaintenanceItemsTableProps) => {
  const getItemRows = (): ItemRow[] => {
    if (pastMaintenance?.items?.length === 0) {
      return [];
    }

    return pastMaintenance!.items!.map((item) => {
      return {
        id: item.id,
        name: item.name.value,
        cost: item.price,
        remark: item.remark,
      };
    });
  };

  const itemRows = getItemRows();

  return (
    <div className={styles.container}>
      <Box className={styles.itemsTable}>
        <BaseTable aria-labelledby='Itens de Manutenções' className={styles.table}>
          <TableHead order='asc' orderBy={itemTableColumns[0]} columns={itemTableColumns} />
          <TableBody>
            {itemRows.length === 0 ? (
              <TableRow className={styles.tableRow}>
                <TableCell>
                  <PlaceholderText>Nenhum item</PlaceholderText>
                </TableCell>
                <TableCell>
                  <PlaceholderText>Não registrado</PlaceholderText>
                </TableCell>
                <TableCell>
                  <PlaceholderText>Nenhuma descrição</PlaceholderText>
                </TableCell>
              </TableRow>
            ) : (
              itemRows.map((row) => {
                return (
                  <TableRow key={row.id} className={styles.tableRow}>
                    {itemTableColumns.map((column) => {
                      return (
                        <TableCell key={`${row.id}.${column.id}`} className={column.classNameFunc ? column.classNameFunc(row) : column.className}>
                          <Typography variant='inherit' noWrap>
                            {column.Cell(row)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </BaseTable>

        <div className={styles.generalRemark}>
          <Label>Observações Gerais</Label>
          {pastMaintenance.generalObservations ? (
            <DescriptionListDefault>{pastMaintenance.generalObservations}</DescriptionListDefault>
          ) : (
            <PlaceholderText>Nenhuma observação</PlaceholderText>
          )}
        </div>
      </Box>
      <Button variant='text' startIcon={<EditRounded />} onClick={() => onEditClick(pastMaintenance)}>
        Editar
      </Button>
    </div>
  );
};

export default VehicleMaintenanceItemsTable;
